"use client";

import { ReactNode, useEffect, useRef, useState } from "react";

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

/**
 * Scroll-reveal fade-up — IntersectionObserver + CSS transition, no
 * animation library.
 *
 * This used to be a framer-motion `whileInView`. It's used on nearly
 * every section across the site, so it was the single biggest reason
 * framer's animation engine had to hydrate on the homepage. A fade-up on
 * first view is just opacity + translateY — both GPU-composited — so an
 * IntersectionObserver toggling one CSS transition does it with zero JS
 * animation runtime. Dropping framer here is what lets the rest of the
 * app run on the lightweight `domAnimation` feature set (which omits the
 * inView feature this once relied on).
 *
 * Behaviour preserved: fade up 20px over 0.35s, optional stagger `delay`,
 * fires once, reduced-motion shows instantly.
 */
const AnimatedSection = ({ children, className = "", delay = 0 }: AnimatedSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect(); // once
        }
      },
      // mirror the old framer viewport margin — reveal as it scrolls in
      { rootMargin: "0px 0px -50px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(20px)",
        transition: `opacity 0.35s cubic-bezier(0.25,0.4,0.25,1) ${delay}s, transform 0.35s cubic-bezier(0.25,0.4,0.25,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

export default AnimatedSection;
